import { useQuery } from "@tanstack/react-query";
import axiosInstance from "@/api/axios-instance";

export interface ProductRegionConfig {
  id: string;
  product_id: string;
  region_id: string;
  onboarding_routing?: any[] | null;
  onboarding_default_bundle_id?: string | null;
  // Per-form presentation for the picker, keyed by Form id.
  onboarding_form_meta?: Record<string, any> | null;
  // Answer→round-robin routing, keyed by managed_event_id.
  round_robin_routing?: Record<string, any> | null;
  images?: Record<string, any> | null;
  metadata?: Record<string, any> | null;
  status: "ACTIVE" | "INACTIVE";
  onboarding_forms?: Array<{ id: string; name: string }>;
  onboarding_default_bundle?: { id: string; name: string } | null;
  // From the endpoint's include — used to name a config's region in the UI.
  region_rel?: { id: string; code: string; display_name: string } | null;
}

export interface ProductRegionConfigInput {
  product_id: string;
  region_id: string;
  onboarding_form_ids?: string[];
  onboarding_routing?: any[];
  onboarding_default_bundle_id?: string | null;
  onboarding_form_meta?: Record<string, any>;
  round_robin_routing?: Record<string, any>;
  images?: Record<string, any>;
  metadata?: Record<string, any>;
  status?: "ACTIVE" | "INACTIVE";
  // Cross-region propagation (update only): also apply these presentation groups
  // to the SAME product's configs in the other regions. Independent toggles, so a
  // save can mirror any subset. Everything else on this form is region-bound and
  // can never be propagated — see PROPAGATION_GROUPS on the server.
  propagate_groups?: PropagationGroup[];
}

// Presentation groups that may be mirrored to a product's other regions.
// MIRRORED from apps/server/src/modules/api/product-region-config/propagation-groups.ts
// — the server validates against the same keys, so adding one means editing both.
export const PROPAGATION_GROUPS = [
  "images",
  "page_details",
  "card",
  "pricing_copy",
] as const;
export type PropagationGroup = (typeof PROPAGATION_GROUPS)[number];

// One config per product+region (unique). Returns the existing row or null.
export async function getProductRegionConfig(params: {
  product_id: string;
  region_id: string;
}): Promise<ProductRegionConfig | null> {
  const r = await axiosInstance.get("/product-region-config", { params });
  return r.data.configs?.[0] ?? null;
}

// All product-region configs for a region (each carries metadata.visible). Used
// to filter a product picker to the ones actually shown on the dashboard.
export async function getProductRegionConfigs(
  region_id: string,
): Promise<ProductRegionConfig[]> {
  const r = await axiosInstance.get("/product-region-config", {
    params: { region_id },
  });
  return r.data.configs ?? [];
}

export function useRegionConfigs(region_id?: string) {
  return useQuery({
    queryKey: ["product-region-config", "region", region_id],
    queryFn: () => getProductRegionConfigs(region_id!),
    enabled: !!region_id,
  });
}

export async function createProductRegionConfig(data: ProductRegionConfigInput) {
  return (await axiosInstance.post("/product-region-config", data)).data;
}

// Bulk set dashboard-card priority for many products in one region (drag-to-reorder).
export async function setRegionConfigPriorities(
  region_id: string,
  items: { product_id: string; priority: number }[],
): Promise<{ updated: number }> {
  return (
    await axiosInstance.patch("/product-region-config/priorities", {
      region_id,
      items,
    })
  ).data;
}

export async function updateProductRegionConfig(
  id: string,
  data: Partial<ProductRegionConfigInput>,
) {
  return (await axiosInstance.patch(`/product-region-config/${id}`, data)).data;
}

// Upload a product image (thumbnail | hero) to Spaces; returns its public CDN
// URL. The caller stores the url into images[kind] and persists via the normal
// create/update save (this endpoint only handles the file bytes).
export async function uploadProductRegionConfigImage(
  file: File,
  product_id: string,
  kind: "thumbnail" | "hero" | "onboarding_form" | "assessment-card",
): Promise<{ url: string }> {
  const form = new FormData();
  form.append("file", file);
  form.append("product_id", product_id);
  form.append("kind", kind);
  const r = await axiosInstance.post(
    "/product-region-config/upload-image",
    form,
    { headers: { "Content-Type": "multipart/form-data" } },
  );
  return r.data;
}

// The SAME product's configs in the OTHER regions — the targets a propagation
// toggle would write to. Reuses the existing list endpoint (product_id only) and
// drops the current region client-side; `region_rel` comes back in the include,
// so the UI can name the regions rather than just counting them.
export function useProductRegionConfigSiblings(
  product_id?: string,
  region_id?: string,
) {
  return useQuery({
    queryKey: ["product-region-config", "siblings", product_id],
    queryFn: async () => {
      const r = await axiosInstance.get("/product-region-config", {
        params: { product_id },
      });
      return (r.data.configs ?? []) as ProductRegionConfig[];
    },
    enabled: !!product_id,
    select: (configs) => configs.filter((c) => c.region_id !== region_id),
  });
}

export function useProductRegionConfig(
  product_id?: string,
  region_id?: string,
) {
  return useQuery({
    queryKey: ["product-region-config", product_id, region_id],
    queryFn: () =>
      getProductRegionConfig({
        product_id: product_id!,
        region_id: region_id!,
      }),
    enabled: !!product_id && !!region_id,
  });
}
